const IMAGE_HOST = "https://api.nftbaz.com";

export function fixBlogImageUrls(html: string): string {
  return html.replace(/src="\/upload\//g, `src="${IMAGE_HOST}/upload/`);
}

// Decode common HTML entities and normalize whitespace / invisible chars so that
// a TOC title (literal Unicode) can be compared against heading inner HTML
// (which often arrives entity-encoded, e.g. "&ndash;", "&zwnj;", "&amp;").
function normalizeHeadingText(input: string): string {
  return input
    .replace(/<[^>]+>/g, "") // strip inner tags like <strong>
    .replace(/&nbsp;/gi, " ")
    .replace(/&ndash;/gi, "–")
    .replace(/&mdash;/gi, "—")
    .replace(/&zwnj;/gi, "‌")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/[‌‏‎ ]/g, "") // drop ZWNJ / bidi / nbsp
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function injectHeadingIds(
  html: string,
  toc: Array<{ id: string; title: string; level: number }>
): string {
  if (!toc.length) return html;

  // Index TOC entries by normalized title for the relevant heading level.
  const byKey = new Map<string, string>();
  for (const item of toc) {
    const tag = item.level === 2 ? "h2" : item.level === 3 ? "h3" : "h4";
    byKey.set(`${tag}::${normalizeHeadingText(item.title)}`, item.id);
  }

  const used = new Set<string>();

  // Walk every h2/h3/h4 in the HTML and add the matching id (only if it has none).
  return html.replace(
    /<(h[234])([^>]*)>([\s\S]*?)<\/\1>/gi,
    (full, tag: string, attrs: string, inner: string) => {
      if (/\sid\s*=/.test(attrs)) return full; // already has an id

      const key = `${tag.toLowerCase()}::${normalizeHeadingText(inner)}`;
      const id = byKey.get(key);
      if (!id || used.has(id)) return full;

      used.add(id);
      return `<${tag}${attrs} id="${id}">${inner}</${tag}>`;
    }
  );
}

export function stripHtml(html?: string | null): string {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&ndash;/gi, "–")
    .replace(/&mdash;/gi, "—")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function wrapTables(html: string): string {
  return html.replace(
    /<table([\s\S]*?)<\/table>/gi,
    '<div class="table-wrapper"><table$1</table></div>'
  );
}

/**
 * Images inside CMS HTML load lazily and decode off the main thread. They sit
 * in the article body, below the title, so loading them eagerly only made them
 * compete with the fonts and the CSS the first screen needs (four ~80 KB images
 * at 1.5 s on a phone). Run AFTER `sanitizeCmsHtml` — the sanitizer drops
 * attributes it does not know — and it only ever adds these two fixed values,
 * never anything from the CMS. An img that already states `loading` is left alone.
 */
export function lazyImages(html: string): string {
  return html.replace(/<img\b(?![^>]*\bloading=)/gi, `<img loading="lazy" decoding="async"`);
}

/**
 * Scroll to a point in the article EXACTLY. Its blocks are laid out only near
 * the screen (content-visibility in blog-index.css), so a smooth scroll that
 * passes them replaces their estimated heights with real ones and the target
 * keeps moving. For the jump the whole article is laid out once (the
 * `is-measuring` class), the position read, the scroll started; the blocks then
 * keep their real heights (`contain-intrinsic-size: auto` remembers them), so
 * nothing shifts afterwards. Client-only — call from an event handler.
 */
export function scrollArticleTo(el: HTMLElement, offset = 0) {
  const article = document.getElementById("article-content");
  article?.classList.add("is-measuring");
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
  const done = () => article?.classList.remove("is-measuring");
  if ("onscrollend" in window) window.addEventListener("scrollend", done, { once: true });
  else setTimeout(done, 1500);
}

export function processArticleHtml(
  html: string,
  toc: Array<{ id: string; title: string; level: number }>
): string {
  let processed = fixBlogImageUrls(html);
  processed = injectHeadingIds(processed, toc);
  processed = wrapTables(processed);
  return processed;
}
